import { v4 as uuidv4 } from "uuid";

import {
  getCatalogSourceLabel,
  getLocalizedFoodName,
  isCatalogFood,
} from "../foods/foodCatalog";
import {
  CATALOG_FOOD_SOURCE_EFORT,
  CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  normalizeCatalogFoodSource,
} from "../foods/supabaseFoodCatalogShared";
import {
  CANONICAL_ALLERGEN_CODES,
  deriveRecipeClassification,
  getRuntimeFoodClassification,
  normalizeAllergenCodes,
} from "../foods/foodClassification";
import {
  buildNutritionRecipeRecord,
  inflateNutritionRecipeRecord,
  normalizeNutritionWebsiteUrl,
} from "../../Firestore/nutrition/nutritionRecords";
import {
  createEmptyMicronutrients,
  MICRONUTRIENT_FIELDS,
} from "../shared/micronutrients";
import {
  getLocalizedCommonMeasureDisplayLabelForMeasure,
  normalizeBuiltInCommonMeasureId,
  normalizeCommonMeasureId,
} from "../shared/commonMeasures";
import { buildNutritionEntityDuplicateName } from "../shared/nutritionEntityNameUtils";
import { UI_MACRO_DISPLAY_ORDER } from "../shared/nutritionFormatters";
import {
  validateRecipeDraftLimits,
} from "../nutritionLimits";
export {
  formatCalorieValue,
  formatMacroValue,
  formatMicronutrientValue,
  formatPercentageValue,
} from "../shared/nutritionFormatters";
export { MICRONUTRIENT_FIELDS };

export const RECIPE_TYPE_OPTIONS = [
  { key: "main-string", icon: "restaurant-outline" },
  { key: "breakfast-string", icon: "cafe-outline" },
  { key: "snack-string", icon: "nutrition-outline" },
];

const SUPPORTED_RECIPE_TYPES = new Set(
  RECIPE_TYPE_OPTIONS.map((option) => option.key),
);
const PERSISTABLE_INGREDIENT_SOURCES = new Set([
  CATALOG_FOOD_SOURCE_EFORT,
  CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  "bedca",
  "coach_created",
  "demo",
]);
const AUTO_CALCULATED_EQUIVALENT_MAX_ENERGY_DELTA_KCAL = 15;
const AUTO_CALCULATED_GRAMS_ROUNDING_STEP = 5;
const AUTO_CALCULATED_MEASURE_ROUNDING_STEP = 0.25;

export const INGREDIENT_SORT_OPTIONS = [
  { key: "default", labelKey: "default-string", field: null },
  { key: "energy", labelKey: "energy-string", field: "calories" },
  ...UI_MACRO_DISPLAY_ORDER.map((field) => ({
    key: field,
    labelKey: `${field}-string`,
    field,
  })),
];

export const INGREDIENT_DATABASE_OPTIONS = [
  { key: "all", labelKey: "allDatabases-string" },
  { key: "createdByMe", labelKey: "createdByMe-string" },
  { key: "catalog", labelKey: "catalogSourceFoodDatabase-string" },
];

export const RECIPE_EDITOR_FIELD_LABEL_LINE_HEIGHT = 16;
export const RECIPE_EDITOR_FORM_COLUMN_GAP = 16;
export const RECIPE_EDITOR_NAME_INPUT_HEIGHT = 42;
export const RECIPE_EDITOR_TYPE_CHIP_HEIGHT = 54;
export const RECIPE_EDITOR_FIELD_TO_CONTROL_GAP = 10;
export const RECIPE_EDITOR_IMAGE_CONTROL_SIZE =
  RECIPE_EDITOR_NAME_INPUT_HEIGHT +
  RECIPE_EDITOR_FORM_COLUMN_GAP +
  RECIPE_EDITOR_FIELD_LABEL_LINE_HEIGHT +
  RECIPE_EDITOR_FIELD_TO_CONTROL_GAP +
  RECIPE_EDITOR_TYPE_CHIP_HEIGHT;

export function getSafeNumber(value) {
  const parsedValue = Number(value ?? 0);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

export function normalizeDecimalInput(value) {
  return String(value ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");
}

function parsePositiveReferenceQuantity(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  const normalizedValue = String(value ?? "").trim().replace(",", ".");
  if (!/^\d*\.?\d+$/.test(normalizedValue)) {
    return null;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

export function parsePositiveNumber(value) {
  const normalizedValue = normalizeDecimalInput(value);

  if (normalizedValue.trim() === "") {
    return null;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

export function normalizeRecipeType(typeKey) {
  const normalizedTypeKey = String(typeKey || "").trim();

  if (
    normalizedTypeKey === "lunch-string" ||
    normalizedTypeKey === "dinner-string"
  ) {
    return "main-string";
  }

  return SUPPORTED_RECIPE_TYPES.has(normalizedTypeKey)
    ? normalizedTypeKey
    : "main-string";
}

export function getRecipeTypeFromMealTypes(mealTypes) {
  const primaryMealType = Array.isArray(mealTypes) ? mealTypes[0] : null;

  if (primaryMealType === "breakfast") {
    return "breakfast-string";
  }

  if (primaryMealType === "snack") {
    return "snack-string";
  }

  return "main-string";
}

export function createEmptyMicros() {
  return createEmptyMicronutrients();
}

export function normalizeMicrosValues(source) {
  const resolvedSource =
    source && typeof source === "object" ? source : createEmptyMicros();
  const normalizedMicros = createEmptyMicros();

  MICRONUTRIENT_FIELDS.forEach((field) => {
    normalizedMicros[field.key] = getSafeNumber(resolvedSource[field.key]);
  });

  return normalizedMicros;
}

export function normalizeCommonMeasures(commonMeasures) {
  if (!Array.isArray(commonMeasures)) {
    return [];
  }

  const seenIds = new Set();

  return commonMeasures
    .map((measure) => {
      const measureKey = normalizeBuiltInCommonMeasureId(
        measure?.measureKey ?? measure?.id,
      ).trim();
      const grams = getSafeNumber(measure?.grams);

      if (!measureKey || grams <= 0 || seenIds.has(measureKey)) {
        return null;
      }

      seenIds.add(measureKey);

      return {
        id: measureKey,
        measureKey,
        grams,
      };
    })
    .filter(Boolean);
}

export function getIngredientMicrosSource(ingredient) {
  if (ingredient?.micros && typeof ingredient.micros === "object") {
    return ingredient.micros;
  }

  return ingredient;
}

export function getIngredientCommonMeasures(ingredient) {
  return normalizeCommonMeasures(
    ingredient?.commonMeasures ?? ingredient?.common_measures,
  );
}

export function getIngredientSelectedUnitKey(ingredient) {
  const quantityUnitKey = String(ingredient?.quantityUnitKey || "grams").trim();
  const normalizedQuantityUnitKey =
    quantityUnitKey === "grams"
      ? "grams"
      : normalizeCommonMeasureId(quantityUnitKey, quantityUnitKey);

  if (normalizedQuantityUnitKey === "grams") {
    return "grams";
  }

  return getIngredientCommonMeasures(ingredient).some(
    (measure) => measure.measureKey === normalizedQuantityUnitKey,
  )
    ? normalizedQuantityUnitKey
    : "grams";
}

export function getQuantityInGramsFromUnit(
  quantityValue,
  quantityUnitKey,
  commonMeasures,
) {
  const quantity = parsePositiveNumber(quantityValue);

  if (quantity == null) {
    return 0;
  }

  const selectedUnitKey = String(quantityUnitKey || "grams").trim() || "grams";
  const normalizedSelectedUnitKey =
    selectedUnitKey === "grams"
      ? "grams"
      : normalizeCommonMeasureId(selectedUnitKey, selectedUnitKey);

  if (normalizedSelectedUnitKey === "grams") {
    return quantity;
  }

  const selectedMeasure = normalizeCommonMeasures(commonMeasures).find(
    (measure) => measure.measureKey === normalizedSelectedUnitKey,
  );
  const gramsPerMeasure = getSafeNumber(selectedMeasure?.grams);

  return gramsPerMeasure > 0 ? quantity * gramsPerMeasure : quantity;
}

export function getIngredientQuantityInGrams(ingredient) {
  return getQuantityInGramsFromUnit(
    ingredient?.quantity,
    getIngredientSelectedUnitKey(ingredient),
    getIngredientCommonMeasures(ingredient),
  );
}

export function formatQuantityInputValue(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  const decimalPlaces = numericValue < 10 ? 2 : 1;
  return String(Number(numericValue.toFixed(decimalPlaces)));
}

export function formatQuantityDisplayValue(
  value,
  { zeroFallback = false } = {},
) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  if (numericValue < 0) {
    return "";
  }

  if (numericValue === 0) {
    return zeroFallback ? "0" : "";
  }

  const roundedTenths =
    Math.round((numericValue + Number.EPSILON) * 10) / 10;

  if (roundedTenths === 0) {
    return zeroFallback ? "0" : "";
  }

  if (roundedTenths >= 10) {
    return String(Math.round(numericValue));
  }

  return Number.isInteger(roundedTenths)
    ? String(roundedTenths)
    : roundedTenths.toFixed(1);
}

export function getQuantityValueFromGrams(
  quantityInGrams,
  quantityUnitKey,
  commonMeasures,
) {
  const normalizedQuantityInGrams = getSafeNumber(quantityInGrams);
  const selectedUnitKey = String(quantityUnitKey || "grams").trim() || "grams";
  const normalizedSelectedUnitKey =
    selectedUnitKey === "grams"
      ? "grams"
      : normalizeCommonMeasureId(selectedUnitKey, selectedUnitKey);

  if (normalizedQuantityInGrams <= 0) {
    return "";
  }

  if (normalizedSelectedUnitKey === "grams") {
    return formatQuantityInputValue(normalizedQuantityInGrams);
  }

  const selectedMeasure = normalizeCommonMeasures(commonMeasures).find(
    (measure) => measure.measureKey === normalizedSelectedUnitKey,
  );
  const gramsPerMeasure = getSafeNumber(selectedMeasure?.grams);

  if (gramsPerMeasure <= 0) {
    return formatQuantityInputValue(normalizedQuantityInGrams);
  }

  return formatQuantityInputValue(normalizedQuantityInGrams / gramsPerMeasure);
}

export function formatQuantityInGramsText(quantityInGrams) {
  const formattedValue = formatQuantityDisplayValue(quantityInGrams, {
    zeroFallback: true,
  });
  return `${formattedValue}g`;
}

export function getQuantityUnitOptions(ingredient, { t } = {}) {
  const commonMeasures = getIngredientCommonMeasures(ingredient);

  return [
    { id: "grams", label: "g" },
    ...commonMeasures.map((measure) => ({
      id: measure.measureKey,
      label: getLocalizedCommonMeasureDisplayLabelForMeasure(measure, { t }),
    })),
  ];
}

function resolveIngredientUnitKey(ingredient, nextUnitKey) {
  const resolvedNextUnitKey = String(nextUnitKey || "grams").trim() || "grams";
  const normalizedNextUnitKey =
    resolvedNextUnitKey === "grams"
      ? "grams"
      : normalizeCommonMeasureId(resolvedNextUnitKey, resolvedNextUnitKey);

  if (normalizedNextUnitKey === "grams") {
    return "grams";
  }

  return getIngredientCommonMeasures(ingredient).some(
    (measure) => measure.measureKey === normalizedNextUnitKey,
  )
    ? normalizedNextUnitKey
    : "grams";
}

export function getInitialFoodQuantityInGrams(food) {
  const candidateValues = [
    food?.quantityReferenceG,
    food?.reference_quantity_g,
    food?.quantity,
  ];

  for (const candidateValue of candidateValues) {
    const parsedQuantity = parsePositiveReferenceQuantity(candidateValue);
    if (parsedQuantity != null) {
      return parsedQuantity;
    }
  }

  return 100;
}

function getInitialFoodSelection(food) {
  const commonMeasures = getIngredientCommonMeasures(food);
  const selectedMeasureId = getIngredientSelectedUnitKey({
    quantityUnitKey:
      food?.selected_measure_id ?? food?.selectedMeasureId ?? food?.quantityUnitKey,
    commonMeasures,
  });
  const quantityInGrams = getInitialFoodQuantityInGrams(food);

  if (selectedMeasureId === "grams") {
    return {
      quantity: String(quantityInGrams),
      quantityInGrams,
      quantityUnitKey: "grams",
      selectedMeasureId: null,
    };
  }

  return {
    quantity:
      getQuantityValueFromGrams(quantityInGrams, selectedMeasureId, commonMeasures) ||
      String(quantityInGrams),
    quantityInGrams,
    quantityUnitKey: selectedMeasureId,
    selectedMeasureId,
  };
}

export function cloneIngredient(ingredient) {
  const commonMeasures = getIngredientCommonMeasures(ingredient);
  const quantityUnitKey = String(ingredient?.quantityUnitKey || "grams").trim();
  const normalizedQuantityUnitKey =
    quantityUnitKey === "grams"
      ? "grams"
      : normalizeCommonMeasureId(quantityUnitKey, quantityUnitKey);
  const classification = getRuntimeFoodClassification(ingredient);

  return {
    ...ingredient,
    quantityUnitKey:
      normalizedQuantityUnitKey === "grams" ||
      commonMeasures.some(
        (measure) => measure.measureKey === normalizedQuantityUnitKey,
      )
        ? normalizedQuantityUnitKey
        : "grams",
    commonMeasures,
    allergens: classification.allergens,
    allergenDataComplete: classification.allergenDataComplete,
    isVegetarian: classification.isVegetarian,
    isVegan: classification.isVegan,
    dietaryDataComplete: classification.dietaryDataComplete,
    fiber: getSafeNumber(ingredient?.fiber),
    micros: normalizeMicrosValues(getIngredientMicrosSource(ingredient)),
    equivalences: Array.isArray(ingredient?.equivalences)
      ? ingredient.equivalences.map(cloneIngredient)
      : [],
  };
}

export function getNutritionTotalsForQuantity(foodLike, quantityInGrams) {
  const factor = getSafeNumber(quantityInGrams) > 0 ? quantityInGrams / 100 : 0;

  return {
    calories: getSafeNumber(foodLike?.calories) * factor,
    carbs: getSafeNumber(foodLike?.carbs) * factor,
    protein: getSafeNumber(foodLike?.protein) * factor,
    fats: getSafeNumber(foodLike?.fats) * factor,
    fiber: getSafeNumber(foodLike?.fiber) * factor,
  };
}

export function getQuantityInGramsForTargetCalories(foodLike, targetCalories) {
  const caloriesPer100g = getSafeNumber(foodLike?.calories);
  const normalizedTargetCalories = getSafeNumber(targetCalories);

  if (caloriesPer100g <= 0 || normalizedTargetCalories <= 0) {
    return 0;
  }

  return (normalizedTargetCalories * 100) / caloriesPer100g;
}

function roundPositiveQuantityToStep(quantityValue, step) {
  const normalizedQuantity = getSafeNumber(quantityValue);
  const normalizedStep = getSafeNumber(step);

  if (normalizedQuantity <= 0 || normalizedStep <= 0) {
    return 0;
  }

  return Number(
    (
      Math.round((normalizedQuantity + Number.EPSILON) / normalizedStep) *
      normalizedStep
    ).toFixed(4),
  );
}

function getAutoCalculatedEquivalentRoundedQuantityInGrams(
  equivalence,
  targetCalories,
  quantityInGrams,
  resolvedUnitKey,
  commonMeasures,
) {
  const normalizedQuantityInGrams = getSafeNumber(quantityInGrams);

  if (normalizedQuantityInGrams <= 0) {
    return 0;
  }

  const getEnergyDeltaForQuantityInGrams = (candidateQuantityInGrams) =>
    Math.abs(
      getNutritionTotalsForQuantity(equivalence, candidateQuantityInGrams).calories -
        getSafeNumber(targetCalories),
    );

  if (resolvedUnitKey === "grams") {
    const roundedQuantityInGrams = roundPositiveQuantityToStep(
      normalizedQuantityInGrams,
      AUTO_CALCULATED_GRAMS_ROUNDING_STEP,
    );

    if (
      roundedQuantityInGrams > 0 &&
      getEnergyDeltaForQuantityInGrams(roundedQuantityInGrams) <=
        AUTO_CALCULATED_EQUIVALENT_MAX_ENERGY_DELTA_KCAL
    ) {
      return roundedQuantityInGrams;
    }

    return normalizedQuantityInGrams;
  }

  const selectedMeasure = normalizeCommonMeasures(commonMeasures).find(
    (measure) => measure.measureKey === resolvedUnitKey,
  );
  const gramsPerMeasure = getSafeNumber(selectedMeasure?.grams);

  if (gramsPerMeasure <= 0) {
    return normalizedQuantityInGrams;
  }

  const roundedQuantityInMeasures = roundPositiveQuantityToStep(
    normalizedQuantityInGrams / gramsPerMeasure,
    AUTO_CALCULATED_MEASURE_ROUNDING_STEP,
  );
  const roundedQuantityInGrams = Number(
    (roundedQuantityInMeasures * gramsPerMeasure).toFixed(4),
  );

  if (
    roundedQuantityInMeasures > 0 &&
    roundedQuantityInGrams > 0 &&
    getEnergyDeltaForQuantityInGrams(roundedQuantityInGrams) <=
      AUTO_CALCULATED_EQUIVALENT_MAX_ENERGY_DELTA_KCAL
  ) {
    return roundedQuantityInGrams;
  }

  return normalizedQuantityInGrams;
}

export function updateIngredientUnitState(ingredient, nextUnitKey) {
  const resolvedUnitKey = resolveIngredientUnitKey(ingredient, nextUnitKey);
  const commonMeasures = getIngredientCommonMeasures(ingredient);
  const quantityValue =
    typeof ingredient?.quantity === "string"
      ? ingredient.quantity
      : normalizeDecimalInput(ingredient?.quantity);
  const quantityInGrams = getQuantityInGramsFromUnit(
    quantityValue,
    resolvedUnitKey,
    commonMeasures,
  );

  return {
    ...ingredient,
    quantity: quantityValue,
    quantityUnitKey: resolvedUnitKey,
    selected_measure_id: resolvedUnitKey === "grams" ? null : resolvedUnitKey,
    quantity_g: quantityInGrams,
  };
}

export function updateIngredientQuantityState(ingredient, nextQuantityValue) {
  const resolvedUnitKey = getIngredientSelectedUnitKey(ingredient);
  const commonMeasures = getIngredientCommonMeasures(ingredient);
  const quantityValue = normalizeDecimalInput(nextQuantityValue);
  const quantityInGrams = getQuantityInGramsFromUnit(
    quantityValue,
    resolvedUnitKey,
    commonMeasures,
  );

  return {
    ...ingredient,
    quantity: quantityValue,
    quantityUnitKey: resolvedUnitKey,
    selected_measure_id: resolvedUnitKey === "grams" ? null : resolvedUnitKey,
    quantity_g: quantityInGrams,
  };
}

export function applyAutoCalculatedEquivalentState(
  equivalence,
  targetCalories,
  nextUnitKey = getIngredientSelectedUnitKey(equivalence),
) {
  const resolvedUnitKey = resolveIngredientUnitKey(equivalence, nextUnitKey);
  const commonMeasures = getIngredientCommonMeasures(equivalence);
  const exactQuantityInGrams = getQuantityInGramsForTargetCalories(
    equivalence,
    targetCalories,
  );
  const quantityInGrams = getAutoCalculatedEquivalentRoundedQuantityInGrams(
    equivalence,
    targetCalories,
    exactQuantityInGrams,
    resolvedUnitKey,
    commonMeasures,
  );
  const quantityValue = getQuantityValueFromGrams(
    quantityInGrams,
    resolvedUnitKey,
    commonMeasures,
  );

  return {
    ...equivalence,
    isAutoCalculated: true,
    quantity: quantityInGrams <= 0 ? "0" : quantityValue,
    quantityUnitKey: resolvedUnitKey,
    selected_measure_id: resolvedUnitKey === "grams" ? null : resolvedUnitKey,
    quantity_g: quantityInGrams,
  };
}

export function markEquivalentAsAutoCalculated(equivalence) {
  return {
    ...equivalence,
    isAutoCalculated: true,
  };
}

export function buildLinkedEquivalentFromReference(referenceIngredient, equivalence) {
  return applyAutoCalculatedEquivalentState(
    equivalence,
    getIngredientTotals(referenceIngredient).calories,
  );
}

export function markAutoCalculatedEquivalences(equivalences) {
  return (Array.isArray(equivalences) ? equivalences : []).map(
    markEquivalentAsAutoCalculated,
  );
}

export function syncAutoCalculatedEquivalences(referenceIngredient, equivalences) {
  return (Array.isArray(equivalences) ? equivalences : []).map((equivalence) =>
    buildLinkedEquivalentFromReference(referenceIngredient, equivalence),
  );
}

export function markIngredientAutoCalculatedEquivalences(ingredient) {
  return {
    ...ingredient,
    equivalences: markAutoCalculatedEquivalences(ingredient?.equivalences),
  };
}

export function syncIngredientAutoCalculatedEquivalences(ingredient) {
  return {
    ...ingredient,
    equivalences: syncAutoCalculatedEquivalences(
      ingredient,
      ingredient?.equivalences,
    ),
  };
}

export function getIngredientTotals(ingredient) {
  const quantity = getIngredientQuantityInGrams(ingredient);
  const baseTotals = getNutritionTotalsForQuantity(ingredient, quantity);
  const ingredientMicros = normalizeMicrosValues(
    getIngredientMicrosSource(ingredient),
  );
  const scaledMicros = createEmptyMicros();

  MICRONUTRIENT_FIELDS.forEach((field) => {
    scaledMicros[field.key] = ingredientMicros[field.key] * (quantity / 100);
  });

  return {
    ...baseTotals,
    micros: scaledMicros,
  };
}

export function getRecipeTotals(ingredients) {
  return (Array.isArray(ingredients) ? ingredients : []).reduce(
    (totals, ingredient) => {
      const ingredientTotals = getIngredientTotals(ingredient);

      MICRONUTRIENT_FIELDS.forEach((field) => {
        totals.micros[field.key] += ingredientTotals.micros[field.key];
      });

      totals.calories += ingredientTotals.calories;
      totals.carbs += ingredientTotals.carbs;
      totals.protein += ingredientTotals.protein;
      totals.fats += ingredientTotals.fats;
      totals.fiber += ingredientTotals.fiber;

      return totals;
    },
    {
      calories: 0,
      carbs: 0,
      protein: 0,
      fats: 0,
      fiber: 0,
      micros: createEmptyMicros(),
    },
  );
}

export function getMacroCalorieBreakdown(mealTotals) {
  const carbsCalories = Number(mealTotals?.carbs ?? 0) * 4;
  const proteinCalories = Number(mealTotals?.protein ?? 0) * 4;
  const fatsCalories = Number(mealTotals?.fats ?? 0) * 9;
  const totalCalories = carbsCalories + proteinCalories + fatsCalories;

  return {
    carbsCalories,
    proteinCalories,
    fatsCalories,
    totalCalories,
  };
}

export function buildFallbackIngredients(recipe, t) {
  const foodNames = Array.isArray(recipe?.foods)
    ? recipe.foods.filter((foodName) => String(foodName || "").trim().length > 0)
    : [];

  if (foodNames.length === 0) {
    return [];
  }

  const splitCount = foodNames.length;

  return foodNames.map((foodName, index) => ({
    id: `${recipe?.id || "recipe"}-ingredient-${index}`,
    stableKey: `${String(foodName).toLowerCase()}-${index}`,
    name: foodName,
    quantity: 100,
    calories: Number(recipe?.calories ?? 0) / splitCount,
    carbs: Number(recipe?.carbs ?? 0) / splitCount,
    protein: Number(recipe?.protein ?? 0) / splitCount,
    fats: Number(recipe?.fats ?? 0) / splitCount,
    fiber: Number(recipe?.fiber ?? 0) / splitCount,
    sourceLabel: recipe?.createdByMe
      ? t("createdByMe-string")
      : t("catalogSourceFoodDatabase-string", "Food database"),
    source: "legacy_snapshot",
    databaseKey: "legacy_snapshot",
    foodId: null,
    catalogFoodId: null,
    micros: createEmptyMicros(),
    commonMeasures: [],
    quantityUnitKey: "grams",
    equivalences: [],
    _fallback: true,
  }));
}

export function createInitialFormState(recipe, t) {
  const ingredients =
    Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0
      ? recipe.ingredients.map(cloneIngredient)
      : Array.isArray(recipe?.items)
        ? inflateNutritionRecipeRecord(recipe).ingredients.map(cloneIngredient)
      : buildFallbackIngredients(recipe, t);
  const typeKey =
    recipe?.typeKey ??
    getRecipeTypeFromMealTypes(recipe?.meal_types ?? recipe?.mealTypes);

  return {
    name: String(recipe?.name || ""),
    description: String(recipe?.description || ""),
    preparationTime: String(
      recipe?.preparationTime ?? recipe?.prep_time_min ?? recipe?.preparation_time_min ?? "",
    ),
    typeKey: normalizeRecipeType(typeKey),
    imageUri: recipe?.imageUri ?? recipe?.image_uri ?? null,
    ingredients,
  };
}

export function buildRecipeDuplicateDraft(
  form = {},
  { language = "en", order } = {},
) {
  const duplicateDraft = {
    name: buildNutritionEntityDuplicateName("recipe", form, language),
    description: String(form?.description ?? ""),
    preparationTime: String(form?.preparationTime ?? ""),
    typeKey: normalizeRecipeType(form?.typeKey),
    imageUri: form?.imageUri ?? null,
    ingredients: (Array.isArray(form?.ingredients) ? form.ingredients : []).map(
      cloneIngredient,
    ),
  };

  if (Number.isFinite(order)) {
    duplicateDraft.order = Number(order);
  }

  return duplicateDraft;
}

export function addIngredientToRecipeForm(
  form = {},
  ingredientToAdd,
  equivalenceTargetId = null,
) {
  const currentIngredients = Array.isArray(form?.ingredients)
    ? form.ingredients
    : [];

  if (!ingredientToAdd || typeof ingredientToAdd !== "object") {
    return {
      ...form,
      ingredients: currentIngredients,
    };
  }

  const nextIngredient = cloneIngredient(ingredientToAdd);

  if (equivalenceTargetId) {
    return {
      ...form,
      ingredients: currentIngredients.map((ingredient) =>
        ingredient.id === equivalenceTargetId
          ? {
              ...ingredient,
              equivalences: [
                ...(Array.isArray(ingredient.equivalences)
                  ? ingredient.equivalences
                  : []),
                nextIngredient,
              ],
            }
          : ingredient,
      ),
    };
  }

  return {
    ...form,
    ingredients: [...currentIngredients, nextIngredient],
  };
}

function cloneIngredientWithFreshIds(ingredient) {
  const clonedIngredient = cloneIngredient(ingredient);
  const clonedEquivalences = Array.isArray(clonedIngredient?.equivalences)
    ? clonedIngredient.equivalences.map(cloneIngredientWithFreshIds)
    : [];

  return {
    ...clonedIngredient,
    id: uuidv4(),
    equivalences: clonedEquivalences,
    ...(clonedEquivalences.length > 0 ? { equivalenceGroupId: uuidv4() } : {}),
  };
}

function normalizeEquivalentSelectionOrigin(equivalentLike) {
  const originSource =
    equivalentLike?.origin && typeof equivalentLike.origin === "object"
      ? equivalentLike.origin
      : {};
  const coachEquivalentId = String(
    originSource?.coach_equivalent_id ??
      originSource?.coachEquivalentId ??
      equivalentLike?.id ??
      "",
  )
    .trim()
    .toLowerCase();
  const catalogEquivalentId = String(
    originSource?.catalog_equivalent_id ?? originSource?.catalogEquivalentId ?? "",
  )
    .trim()
    .toLowerCase();

  if (catalogEquivalentId && !coachEquivalentId) {
    return { catalogEquivalentId };
  }

  if (coachEquivalentId && !catalogEquivalentId) {
    return { coachEquivalentId };
  }

  return null;
}

export function buildIngredientFromEquivalentSelection(equivalentLike) {
  const equivalentOptions = Array.isArray(equivalentLike?.options)
    ? equivalentLike.options.map(cloneIngredientWithFreshIds).filter(Boolean)
    : [];

  if (equivalentOptions.length === 0) {
    return null;
  }

  const [primaryOption, ...alternativeOptions] = equivalentOptions;
  const normalizedEquivalentName = String(equivalentLike?.name ?? "").trim();
  const normalizedEquivalentId = uuidv4();

  return {
    ...primaryOption,
    equivalenceGroupId: normalizedEquivalentId,
    equivalenceName: normalizedEquivalentName || primaryOption?.name || "",
    equivalenceOrigin: normalizeEquivalentSelectionOrigin(equivalentLike),
    equivalences: syncAutoCalculatedEquivalences(
      primaryOption,
      alternativeOptions,
    ),
  };
}

export function buildIngredientsFromRecipeSelection(recipeLike) {
  if (Array.isArray(recipeLike?.ingredients) && recipeLike.ingredients.length > 0) {
    return recipeLike.ingredients.map(cloneIngredientWithFreshIds);
  }

  if (Array.isArray(recipeLike?.items) && recipeLike.items.length > 0) {
    return inflateNutritionRecipeRecord(recipeLike).ingredients.map(
      cloneIngredientWithFreshIds,
    );
  }

  return [];
}

export function addEquivalentToRecipeForm(form = {}, equivalentLike) {
  const nextIngredient = buildIngredientFromEquivalentSelection(equivalentLike);

  if (!nextIngredient) {
    return {
      ...form,
      ingredients: Array.isArray(form?.ingredients) ? form.ingredients : [],
    };
  }

  return addIngredientToRecipeForm(form, nextIngredient);
}

export function getFoodStableKey(food, language = "en") {
  return String(
    food?.id ||
      food?.code ||
      getLocalizedFoodName(food, language) ||
      food?.name ||
      "food",
  );
}

export function getIngredientSourceLabel(food, t) {
  if (isCatalogFood(food)) {
    return getCatalogSourceLabel(food, t);
  }

  const customLabel = [food?.brand, food?.store]
    .filter((value) => typeof value === "string" && value.trim().length > 0)
    .join("  •  ");

  return (
    customLabel ||
    (typeof t === "function"
      ? t("createdByMe-string", "Created by me")
      : "Created by me")
  );
}

export function buildIngredientFromFood(food, quantity, language, t) {
  const isCatalogSource = isCatalogFood(food);
  const classification = getRuntimeFoodClassification(food);
  const catalogSource = isCatalogSource
    ? normalizeCatalogFoodSource(food?.catalogSource ?? food?.source, {
        defaultSource: CATALOG_FOOD_SOURCE_OPEN_SOURCE,
      })
    : null;

  return {
    id: uuidv4(),
    stableKey: getFoodStableKey(food, language),
    name: getLocalizedFoodName(food, language) || String(food?.name || ""),
    quantity,
    calories: getSafeNumber(food?.calories),
    carbs: getSafeNumber(food?.carbs),
    protein: getSafeNumber(food?.protein),
    fats: getSafeNumber(food?.fats),
    fiber: getSafeNumber(food?.fiber),
    sourceLabel: getIngredientSourceLabel(food, t),
    databaseKey: isCatalogSource ? "catalog" : "createdByMe",
    source: isCatalogSource ? catalogSource : "coach_created",
    foodId: isCatalogSource ? null : food?.id || null,
    catalogFoodId: isCatalogSource ? food?.id || food?.code || null : null,
    brand:
      typeof food?.brand === "string" && food.brand.trim().length > 0
        ? food.brand.trim()
        : null,
    store:
      typeof food?.store === "string" && food.store.trim().length > 0
        ? food.store.trim()
        : null,
    website: normalizeNutritionWebsiteUrl(food?.website),
    allergens: classification.allergens,
    allergenDataComplete: classification.allergenDataComplete,
    isVegetarian: classification.isVegetarian,
    isVegan: classification.isVegan,
    dietaryDataComplete: classification.dietaryDataComplete,
    micros: normalizeMicrosValues(getIngredientMicrosSource(food)),
    commonMeasures: getIngredientCommonMeasures(food),
    quantityUnitKey: "grams",
    equivalences: [],
  };
}

export function buildIngredientFromSelectedFood(food, language, t) {
  const initialSelection = getInitialFoodSelection(food);

  return {
    ...buildIngredientFromFood(
      food,
      initialSelection.quantity,
      language,
      t,
    ),
    quantityUnitKey: initialSelection.quantityUnitKey,
    selected_measure_id: initialSelection.selectedMeasureId,
    quantity_g: initialSelection.quantityInGrams,
  };
}

export function isFallbackIngredient(ingredient) {
  return (
    ingredient?._fallback === true || ingredient?.source === "legacy_snapshot"
  );
}

function isPersistableIngredientSource(ingredient) {
  return PERSISTABLE_INGREDIENT_SOURCES.has(
    String(ingredient?.source || "").trim(),
  );
}

function canPersistIngredientWithZeroQuantity(ingredient, options = {}) {
  return (
    options?.allowZeroAutoCalculatedEquivalent === true &&
    ingredient?.isAutoCalculated === true &&
    getIngredientQuantityInGrams(ingredient) === 0
  );
}

export function sanitizeIngredientForSave(ingredient, options = {}) {
  const quantity = getIngredientQuantityInGrams(ingredient);

  if (
    (quantity <= 0 && !canPersistIngredientWithZeroQuantity(ingredient, options)) ||
    isFallbackIngredient(ingredient) ||
    !isPersistableIngredientSource(ingredient)
  ) {
    return null;
  }

  const clonedIngredient = cloneIngredient(ingredient);

  return {
    ...clonedIngredient,
    quantity,
    equivalences: Array.isArray(ingredient?.equivalences)
      ? ingredient.equivalences
          .map((equivalence) =>
            sanitizeIngredientForSave(equivalence, {
              allowZeroAutoCalculatedEquivalent: true,
            }),
          )
          .filter(Boolean)
      : [],
  };
}

export function getRecipeDraftValidity(form = {}) {
  const ingredients = Array.isArray(form?.ingredients) ? form.ingredients : [];
  const ingredientsForSave = ingredients.map(sanitizeIngredientForSave).filter(Boolean);
  const hasName = String(form?.name ?? "").trim().length > 0;
  const hasFallbackIngredients = ingredients.some(isFallbackIngredient);
  const hasInvalidIngredientQuantities = ingredients.some(
    (ingredient) =>
      !isFallbackIngredient(ingredient) &&
      parsePositiveNumber(ingredient?.quantity) == null,
  );
  const hasUnsaveableIngredients = ingredientsForSave.length !== ingredients.length;
  const limitsValidation = validateRecipeDraftLimits(
    buildNutritionRecipeRecord({
      id: form?.id ?? "recipe",
      name: String(form?.name ?? "").trim(),
      description: String(form?.description ?? "").trim(),
      preparationTime: String(form?.preparationTime ?? "").trim(),
      imageUri: form?.imageUri ?? null,
      meal_types: [
        form?.typeKey === "breakfast-string"
          ? "breakfast"
          : form?.typeKey === "snack-string"
            ? "snack"
            : "main",
      ],
      ingredients: ingredientsForSave,
    }),
  );
  const canSave =
    (ingredientsForSave.length > 0 || hasName) &&
    !hasUnsaveableIngredients &&
    !hasInvalidIngredientQuantities &&
    limitsValidation.ok;

  return {
    canSave,
    ingredientsForSave,
    hasFallbackIngredients,
    hasInvalidIngredientQuantities,
    hasUnsaveableIngredients,
    limitsValidation,
  };
}

export function isRecipeDraftSemanticallyEmpty(form = {}) {
  return (
    String(form?.name ?? "").trim().length === 0 &&
    (Array.isArray(form?.ingredients) ? form.ingredients : []).length === 0
  );
}

export function hasRecipeUsablePickerItems(recipeLike = {}) {
  const ingredients = Array.isArray(recipeLike?.ingredients) ? recipeLike.ingredients : [];
  const items = Array.isArray(recipeLike?.items) ? recipeLike.items : [];
  const foods = Array.isArray(recipeLike?.foods) ? recipeLike.foods : [];

  if (ingredients.length > 0) {
    return ingredients.map(sanitizeIngredientForSave).filter(Boolean).length > 0;
  }

  if (items.length > 0) {
    return buildIngredientsFromRecipeSelection(recipeLike).length > 0;
  }

  if (foods.length > 0) {
    return true;
  }

  return false;
}

export function buildRecipeRecordForSave(form = {}, recipe = {}) {
  const { canSave, ingredientsForSave } = getRecipeDraftValidity(form);
  if (!canSave) {
    return null;
  }

  return buildNutritionRecipeRecord({
    ...recipe,
    id: recipe?.id || uuidv4(),
    order: recipe?.order,
    name: String(form?.name ?? "").trim(),
    description: String(form?.description ?? "").trim(),
    preparationTime: String(form?.preparationTime ?? "").trim(),
    imageUri: form?.imageUri ?? null,
    meal_types: [
      form?.typeKey === "breakfast-string"
        ? "breakfast"
        : form?.typeKey === "snack-string"
          ? "snack"
          : "main",
    ],
    ingredients: ingredientsForSave,
    origin: recipe?.origin,
    _rev: recipe?._rev,
  });
}

export function getRecipeDerivedClassification(ingredients, overrides = {}) {
  const derivedClassification = deriveRecipeClassification(ingredients, {
    allergenOverrideAdditions: overrides.allergenOverrideAdditions,
    allergenOverrideRemovals: overrides.allergenOverrideRemovals,
  });

  return {
    allergens: derivedClassification.allergen_codes,
    allergenDataComplete: derivedClassification.allergen_data_complete,
    isVegetarian: derivedClassification.is_vegetarian,
    isVegan: derivedClassification.is_vegan,
    dietaryDataComplete: derivedClassification.dietary_data_complete,
  };
}

export function getRecipeAllergenOverrideState(allergenCode, ingredients, overrides = {}) {
  const normalizedCode = String(allergenCode ?? "").trim().toLowerCase();
  const baseClassification = deriveRecipeClassification(ingredients, {});
  const overrideAdditions = normalizeAllergenCodes(overrides.allergenOverrideAdditions);
  const overrideRemovals = normalizeAllergenCodes(overrides.allergenOverrideRemovals);
  const isDerived = baseClassification.allergen_codes.includes(normalizedCode);
  const isAdded = overrideAdditions.includes(normalizedCode);
  const isRemoved = overrideRemovals.includes(normalizedCode);

  return {
    isDerived,
    isAdded,
    isRemoved,
    isActive: (isDerived || isAdded) && !isRemoved,
  };
}

export { CANONICAL_ALLERGEN_CODES };

export function matchesIngredientDatabaseFilter(food, databaseKey) {
  if (databaseKey === "all") {
    return true;
  }

  if (databaseKey === "catalog") {
    return isCatalogFood(food);
  }

  if (databaseKey === "createdByMe") {
    return !isCatalogFood(food);
  }

  return true;
}
